import { useEffect, useState, type ReactNode } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";

import { PennButton, PennText } from "@/components/ui";
import { useMe } from "@/hooks/useMe";
import { ADMIN_FORBIDDEN_EVENT } from "@/services/AxiosService";

/** The two admin-console permissions a route group can require (rule §D.1). */
type AdminPermission = "canManageUsers" | "canManageMasterData";

/**
 * Shown in place of a page's content (sidebar/header stay mounted — the user
 * DOES belong in the admin console, just not on this specific page) when the
 * caller lacks the permission a route group requires.
 */
const PageAccessDenied = () => {
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-center min-h-[60vh] px-4">
      <div className="text-center max-w-md">
        <PennText as="h1" size="lg" align="center" color="blue_grey1" font="semibold" className="mb-2">
          You don't have access to this page
        </PennText>
        <PennText as="p" size="sm" align="center" color="blue_grey" className="mb-6">
          Your role doesn't include the permission required here. Contact an
          administrator if you believe this is a mistake.
        </PennText>
        <PennButton variant="secondary" size="md" onClick={() => navigate("/admin", { replace: true })}>
          Back to Admin
        </PennButton>
      </div>
    </div>
  );
};

/**
 * Route-level permission gate for a GROUP of `/admin` routes (layout route —
 * mount as `<Route element={<RequirePagePermission permission="..." />}>`
 * wrapping the group's `<Route>` children, which render via `<Outlet />`).
 *
 * `RequireAdminAccess` only checks the caller holds AT LEAST ONE of
 * `canManageUsers` / `canManageMasterData` to enter the console at all; it
 * does NOT stop someone with only `canManageMasterData` from navigating
 * straight to a `canManageUsers`-only page (e.g. `/admin/users`) by URL —
 * the page would render and its data fetches would 403. This closes that
 * gap per-route-group, mirroring `Sidebar.tsx`'s `isSectionVisible` mapping:
 * "Management" needs `canManageUsers`; "Dashboard"/"Datasets"/"Integrations"
 * need `canManageMasterData`. "Settings" (Profile) stays ungated — visible
 * to any signed-in admin-console user regardless of either permission.
 *
 * Reads the same server-authoritative `/v1/users/me` query as
 * `RequireAdminAccess` (cached — no extra network call) since a cached/local
 * copy of the permission booleans must never decide access (§L).
 */
export const RequirePagePermission = ({ permission }: { permission: AdminPermission }) => {
  const { data, isLoading } = useMe();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-sm text-gray-400">
        Checking access…
      </div>
    );
  }

  const role = data?.user.role;
  const allowed = role?.[permission] === true;

  if (!allowed) {
    return <PageAccessDenied />;
  }

  return <Outlet />;
};

/**
 * Catch-all 403 net for the admin console. The route-level gates above cover
 * the mapped sections, but any admin API can still 403 (a page whose route
 * gate passes may call an endpoint requiring a different permission, or the
 * caller's role could change mid-session). The admin Axios service fires
 * `ADMIN_FORBIDDEN_EVENT` on every 403 response; this boundary — mounted in
 * `DashboardLayout` around the routed page — swaps the page content for the
 * standard "no permission" panel (sidebar/header stay), and resets on the
 * next navigation so the user can move on to a page they CAN use.
 */
export const ForbiddenBoundary = ({ children }: { children: ReactNode }) => {
  const [forbidden, setForbidden] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const handler = () => setForbidden(true);
    window.addEventListener(ADMIN_FORBIDDEN_EVENT, handler);
    return () => window.removeEventListener(ADMIN_FORBIDDEN_EVENT, handler);
  }, []);

  // A navigation is a fresh start — clear the denied state for the new page.
  useEffect(() => {
    setForbidden(false);
  }, [pathname]);

  if (forbidden) {
    return <PageAccessDenied />;
  }
  return <>{children}</>;
};

/**
 * `/admin` index route target. Previously a blind `<Navigate to="/admin/users">`
 * — a `canManageMasterData`-only caller (no `canManageUsers`) would land on
 * the Users page and immediately hit 403s on its data fetches, since
 * `RequireAdminAccess` only checks "has AT LEAST ONE" of the two permissions.
 * Picks the first page the caller actually has access to instead.
 */
export const AdminIndexRedirect = () => {
  const { data, isLoading } = useMe();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-sm text-gray-400">
        Checking access…
      </div>
    );
  }

  const role = data?.user.role;
  if (role?.canManageUsers === true) return <Navigate to="/admin/users" replace />;
  if (role?.canManageMasterData === true) return <Navigate to="/admin/analytics" replace />;
  // RequireAdminAccess guarantees at least one of the two above, so this is
  // an unreachable-in-practice fallback — Settings/Profile is always visible.
  return <Navigate to="/admin/settings/profile" replace />;
};
